import { t, Trans } from "@lingui/macro";
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef
} from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  CardTitle,
  CardText,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ButtonGroup
} from "reactstrap";
import { Flex } from "antd";
import { Link } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import startsWith from 'lodash/startsWith'
import endsWith from 'lodash/endsWith'

import useAccessCheck, { checkAccess } from "../../util/useAccessCheck";
import useResourceLoader from "../../util/useResourceLoader";
import UWEEntityApi from "../../api/UWEEntityApi";
import UweApi from "../../api/UweApi";
import Loader from "../Loader";
import Notification from "../Notification";
import useTitle from "../../util/useTitle";
import Pagination from "../Pagination";
import useSearchParams from "../../util/useSearchParams";
import SearchBar from "../Search/SearchBar";
import SortButton from "../SortButton";
import { AuthContext } from "../../context/AuthContext";
import { getObject, interpolate, mapObject } from "../../util/mapObject";
import WorkTrayIcon from "./WorkTrayIcon";
import parseFormDefinition from "../../util/parseFormDefinition";
import ElementIcon from "../ElementIcon";
import { useRouteInterpolation } from "../../util/routeUtil";
import { IDENTITY_FORMATTER, useFormatter } from "../../util/applyFormatting";
import Jnx, { useJnx } from "../../util/jnx";
import Diagram from "./Diagram";
import useLoader from "../../util/useLoader";
import { FilterForm } from "../FormComponent";

function WorkTray(props) {
  const ref = useRef();
  const { element, scope: propScope } = props;
  const [lastReload, setLastReload] = useState();
  const scope = useMemo(
    () => ({
      ...propScope,
      ...element?.scope,
      lastReload,
      reload: () => setLastReload(new Date().getTime())
    }),
    [propScope, lastReload, setLastReload, element]
  );
  const [reportEnabled, setReportEnabled] = useState(true);

  const navigate = useNavigate();

  useTitle(element.title);

  const auth = useContext(AuthContext);
  const { user: { user: { role: { extras } = {} } = {} } = {} } = auth;

  const formContext = { auth };

  const exportable = element.columns.some(column => {
      if (column.exportable) {
          return true;
      }
      return false;
  })
  const headerCols = exportable ? 9 : 11;

  const [formDefinition, sFilters] = parseSearchFormDefinition(element);

  const [filters, setFilters] = useState({...sFilters, ...(extras ? JSON.parse(extras).defaultView : {}) });

  function formatTimeByRegion(dateTime, region) {
    if (!dateTime) return "---";
    const timezone = region === "Windmar Puerto Rico" ? "America/Puerto_Rico" : "America/New_York";
    const utcDate = dateTime.includes('T') ? new Date(dateTime) : new Date(`${dateTime} UTC`);
    return utcDate.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  function calculateShiftHours(allocation) {
    let totalMinutes = 0;
    
    // Calculate first shift duration
    if (allocation?.clockIn) {
      const clockOut = allocation?.clockOut || allocation?.adminClockOut;
      if (clockOut) {
        const start = new Date(allocation.clockIn);
        const end = new Date(clockOut);
        totalMinutes += (end - start) / (1000 * 60);
      }
    }
    
    // Calculate second shift duration if available
    if (allocation?.secondClockIn) {
      const secondClockOut = allocation?.secondClockOut || allocation?.adminClockOut;
      if (secondClockOut) {
        const start = new Date(allocation.secondClockIn);
        const end = new Date(secondClockOut);
        totalMinutes += (end - start) / (1000 * 60);
      }
    }
    
    if (totalMinutes <= 0) return "---";

    let hours = Math.floor(totalMinutes / 60);
    let minutes = Math.round(totalMinutes % 60);
    
    // Handle case where rounding minutes results in 60
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  function extractCommonFields(item) {
    return {
      "Shift ID": item.id,
      "Shift Name": item.metadata?.name ?? "---",
      "Location": item.metadata?.locationName ?? "---",
      "Shift Type": item.metadata?.jobTypeTitle ?? "---",
      "Date": item.metadata?.shiftDate ?? "---",
      "Start Time": item.metadata?.startTime ? item.metadata.startTime.slice(0, 5) : "---",
      "End Time": item.metadata?.endTime ? item.metadata.endTime.slice(0, 5) : "---",
      "Region": item.metadata?.region ?? "---"
    };
  }


  const exportTray = async (e) => {
      e.preventDefault();
      if (!reportEnabled) {
          return;
      }
      setReportEnabled(false);
      const exportableColumns = element.columns.filter((column) => column.exportable);
      const resource = lookup.entity ? `uwe-entities/${lookup.entity}/list` : lookup.api.split(':')[1];
      if (!resource) return null;
      mergeParamsIntoFilters(filters, lookup?.params);
      const data = await UWEEntityApi.getWorkTray({ resource, offset: 0, size:200000, sortBy, ...filters });
      let exportDataArray = [];
      if(lookup.entity === "Shift Instance"){
        exportDataArray = data.items.map(item => {
          let allocations = [];
        
          try {
            allocations = JSON.parse(item.metadata.shiftAllocations);
          } catch (e) {
            allocations = [];
          }
        
          // If no allocations, still return one row with "---" values for those fields
          if (!Array.isArray(allocations) || allocations.length === 0) {
            return {
              "Name": "---",
              "Email": "---",
              ...extractCommonFields(item),
              "User Id": "---",
              "Status": "---",
              "Clock In": "---",
              "Admin Clock In": "---",
              "Clock Out": "---",
              "Admin Clock Out": "---",
              "Second Clock In": "---",
              "Admin Second Clock In": "---",
              "Second Clock Out": "---",
              "Admin Second Clock Out": "---",
              "Admin Clock Out Time": "---",
              "Auto clocked-out": "---",
              "Shift Hours": "---",
              "Reason For Leaving": "---"
            };
          }
          // For each allocation, create a row with extracted fields
          return allocations.map(allocation => ({
            "Name": allocation?.userId?.name ?? "---",
            "Email": allocation?.userId?.email ?? "---",
            ...extractCommonFields(item),
            "User Id": allocation?.userId?.id ?? "---",
            "Status": allocation?.status ?? "---",
            "Clock In": formatTimeByRegion(allocation?.clockIn, item.metadata?.region),
            "Admin Clock In": allocation?.adminClockInFlag === true ? "Yes" : "No",
            "Clock Out": formatTimeByRegion(allocation?.clockOut, item.metadata?.region),
            "Admin Clock Out": allocation?.adminClockOutFlag === true ? "Yes" : "No",
            "Second Clock In": formatTimeByRegion(allocation?.secondClockIn, item.metadata?.region),
            "Admin Second Clock In": allocation?.adminSecondClockInFlag === true ? "Yes" : "No",
            "Second Clock Out": formatTimeByRegion(allocation?.secondClockOut, item.metadata?.region),
            "Admin Second Clock Out": allocation?.adminSecondClockOutFlag === true ? "Yes" : "No",
            "Admin Clock Out Time": formatTimeByRegion(allocation?.adminClockOut, item.metadata?.region),
            "Auto clocked-out": allocation?.autoClockout === true ? "Yes" : "No" ?? "---",
            "Shift Hours": calculateShiftHours(allocation),
            "Reason For Leaving": allocation?.reasonForLeaving ?? "---",
          }));
        }).flat();
      }
      else{
        exportDataArray = data.items.map((item) =>{
          return exportableColumns.reduce((acc, column) => {
              let result = column.sortkey?.split('.').reduce((prev, curr) => {
                  return prev ? prev[curr] : null
              }, item)
              if (startsWith(result, '[') && endsWith(result, ']')) {
                try {
                  const resultAsJson = JSON.parse(result)
                  result = `${resultAsJson.map(arrayItem => arrayItem).join(', ')}`
                } catch (e) {
                  // result is not json, continue...
                }
              }
              acc[column.title] = result;
              return acc;
          }, {})
        })
      }
      
      if (Array.isArray(exportDataArray)) {
          const csvStr = JSONtoCSV(exportDataArray);
          downloadCSV(csvStr);
      }
      setReportEnabled(true);
  }

  function JSONtoCSV(jsonArray, headers) {
      if (jsonArray === null || jsonArray === undefined) { return null };
      const replacer = (key, value) => value === null ? '' : value
      const header = Object.keys(jsonArray[0])
      let csv = jsonArray.map(row => header.map(fieldName =>
          JSON.stringify(row[fieldName], replacer)).join(','))
      csv.unshift(header.join(','))
      csv = csv.join('\r\n')
      return csv;
  }

  function downloadCSV(csvStr) {

      const hiddenElement = document.createElement('a');
      const universalBOM = "\uFEFF";
      const blobData = new Blob([universalBOM + csvStr], { type: 'text/csv' });
      const blobURL = URL.createObjectURL(blobData);
      hiddenElement.href = blobURL;
      hiddenElement.target = '_blank';
      hiddenElement.download = `report.csv`;
      hiddenElement.click();
  }

  const [
    [offset, setOffset],
    [size, setSize],
    [sortBy, setSortBy]
  ] = useSearchParams({
    offset: 0,
    size: 10,
    sortBy: element.defaultFilter
  });

  const { lookup, isSelected, setSelected } = useWorktrayHooks(props);

  function mergeParamsIntoFilters(filters, params) {
    if (!params || typeof params !== "object") {
      return filters;
    }

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        filters[key] = value
          .filter(Boolean)
          .map(String)
          .join(",");
      } else if (value != null && typeof value !== "object") {
        filters[key] = String(value);
      }
    });

    return filters;
  }

  const [paging, loading, error] = useResourceLoader(async () => {
    const entity = Array.isArray(lookup.entity)
      ? lookup.entity.join(",")
      : lookup.entity;
    const resource = entity
      ? `uwe-entities/${encodeURIComponent(entity)}/list`
      : lookup?.api?.split(":")?.[1];
    if (!resource) return null;
    mergeParamsIntoFilters(filters, lookup?.params);
    const data = await UWEEntityApi.getWorkTray({
      resource,
      offset,
      size,
      sortBy,
      ...filters,
      "tray": window.location.href.split("/")[4],
    });
    return Array.isArray(data)
      ? {
          from: 0,
          to: data.length,
          count: data.length,
          items: data
        }
      : data;
  }, [size, offset, sortBy, filters, element, lookup, lastReload]);

  const { from = 0, to = 0, count = 0, items: data = [] } = paging || {};

  const onSearch = update => {
    setFilters({
      ...filters,
      ...update.formData
    });
  };

  const [tab, setTab] = useState("table");

  return (
    <Flex
      className={`worktray ${element[":classNames"]}`}
      onClick={e => {
        e.preventDefault();
      }}
      role="table"
      style={{ flexDirection: "column" }}
    >
      {element.canCreate ? <WorkTrayIcon element={element.canCreate} /> : null}

      <div className="worktray-header">
        <Row style={{ width: '100%' }} justify="space-between">
          <Col>
            <div className={`title ${element.tableActions ? "has-buttons" : ""}`}>
              <h2>{element.title}</h2>
            </div>
          </Col>
          <Col justify="flex-end">
            <div className="worktray-header-actions">
              {element.tableActions ? (
                <ActionButtonsCell actions={element.tableActions} scope={scope} />
              ) : null}
              {exportable ? (
                <div className="float-right hide-when-mobile">
                  <Button className="worktray-action-btn" onClick={exportTray} type="button" outline color="secondary" active={reportEnabled}>
                      {
                          reportEnabled ? (
                              <Trans>Export Report</Trans>
                          ) : (
                              <Loader centered sm><Trans>Exporting...</Trans></Loader>
                          )
                      }
                  </Button>
              </div>
              ) : null}
              {element.canSeeDiagram ? (
                <div className="float-right">
                  {tab !== "tree" ? (
                    <Button color="secondary" onClick={() => setTab("tree")}>
                      <i className="fa fa-network-wired" />
                      Ver Diagrama
                    </Button>
                  ) : (
                    <Button color="secondary" onClick={() => setTab("table")}>
                      <i className="fa fa-table" /> Ver Tabla
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </Col>
        </Row>
      </div>

      {formDefinition != null && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
            marginTop: 16
          }}
        >
          <small style={{ color: "gray", fontSize: "10pt" }}>
            <Trans>Filters:</Trans>
          </small>
          <FilterForm
            className="worktray-filters"
            tagName="div"
            ref={ref}
            formData={filters}
            formContext={{...formContext, ...filters}}
            {...formDefinition}
            onChange={onSearch}
          />
        </div>
      )}
      <Card className="inbox-card worktray-content" style={{ flexGrow: 1, minHeight: 0 }}>
        {element.canSeeDiagram && tab === "tree" ? (
          <Diagram
            data={data}
            dataDiagram={element.canSeeDiagramnt}
            navigate={navigate}
          />
        ) : (
          <Flex style={{ flexDirection: "column", height: "100%" }}>
            <div style={{ overflowY: "scroll", flexGrow: 1, minHeight: 0 }}>
              <Table className="hide-when-mobile" striped>
                <thead>
                  <tr>
                    {element.columns.reduce((acc, { sortkey, title, style, headerStyle }, idx) => {
                      if (!element.columns[idx].hideColumn) {
                          acc.push(sortkey ? (
                              <SortButton
                                key={idx}
                                tag="th"
                                sortKey={sortkey}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                style={{
                                  ...(style || headerStyle),
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 1,
                                  backgroundColor: 'white',
                                  cursor: "pointer",
                                  border: 'none'
                                }}
                              >{title}</SortButton>
                          ) : (
                              <th
                                key={idx}
                                style={{
                                  ...(style || headerStyle),
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 1,
                                  backgroundColor: 'white',
                                  border: 'none'
                                }}
                              >{title}</th>
                          ))
                      }
                      return acc;
                  }, [])}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <>
                      {/* Add empty row so loader doesnt show with gray background */}
                      <tr />
                      <tr>
                        <td colSpan="8" style={{ paddingTop: 100 }}>
                          <Loader centered>
                            <Trans>Loading {element.title}</Trans>
                          </Loader>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <>
                      {error ? (
                        <tr>
                          <td colSpan="8">
                            <Notification error={error} />
                          </td>
                        </tr>
                      ) : null}
                      {data && data.length ? (
                        data.map((rowObject, idx) => (
                          <DataRow
                            key={idx}
                            columns={element.columns}
                            rowObject={rowObject}
                            scope={scope}
                            selected={isSelected(rowObject)}
                            setSelected={setSelected}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7">
                            <Notification color="warning">
                              <Trans>The list of {element.title} is empty.</Trans>
                            </Notification>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </Table>
            </div>
            <div className="mobile-table show-when-mobile">
              {loading ? (
                <Loader centered>
                  <Trans>Loading {element.title}</Trans>
                </Loader>
              ) : (
                <>
                  {data && data.length ? (
                    data.map((rowObject, idx) => (
                      <MobileDataRow
                        key={idx}
                        columns={element.columns}
                        rowObject={rowObject}
                        scope={scope}
                        selected={isSelected(rowObject)}
                        setSelected={setSelected}
                      />
                    ))
                  ) : (
                    <Notification color="warning">
                      <Trans>The list of {element.title} is empty.</Trans>
                    </Notification>
                  )}
                </>
              )}
            </div>
            {!loading && (
              <CardFooter className="pagination">
                <Pagination
                  offset={from}
                  count={count}
                  size={size}
                  setSize={setSize}
                  setOffset={setOffset}
                />
              </CardFooter>
            )}
          </Flex>
        )}
      </Card>
    </Flex>
  );
}

function useWorktrayHooks({
  element,
  selected: propSelected,
  setSelected: propSetSelected
}) {
  const { selectable } = element;
  const auth = useContext(AuthContext);

  const _lookup = useMemo(() => {
    let lookup = null;
    if (element.lookup) {
      lookup = { ...element.lookup };
    } else {
      let entityType;
      if (element.useMainRoleEntities) {
        const userRoles = auth?.user?.user?.roles || [];
        const mainRole = userRoles.find(x => x.mainRole) ?? userRoles[0];
        entityType = Object.entries(mainRole?.role?.entities ?? {})
          .map(([key, { homeTray }]) => (homeTray ? key : null))
          .filter(x => !!x);
      } else {
        entityType = element.entityType;
        if (element.otherEntityTypes?.length) {
          entityType = [entityType, ...element.otherEntityTypes].filter(
            x => !!x
          );
        }
      }
      lookup = {
        entity: entityType,
        api:
          !element.entityType && element.resource
            ? `api:${element.resource}`
            : null,
        params: element.lookupParams
      };
    }

    const paramsJnx = {};
    if (lookup?.params) {
      Object.entries(lookup.params).forEach(([k, v]) => {
        if (k.startsWith("expr:")) {
          delete lookup.params[k];
          k = k.replace(/^expr:/, "");
          paramsJnx[k] = new Jnx(v);
        }
      });
    }

    return { ...lookup, paramsJnx };
  }, [element?.lookup, element, auth]);
  const idAttr = _lookup.id;

  const bindings = useMemo(
    () => ({
      auth,
      idAttr
    }),
    [auth, idAttr]
  );

  const lookup = useMemo(
    () => ({
      ..._lookup,
      params: {
        ..._lookup.params,
        ...Object.entries(_lookup.paramsJnx).reduce((_, [k, jnx]) => {
          _[k] = jnx.eval({}, "", bindings);
          return _;
        }, {})
      }
    }),
    [_lookup]
  );

  const { isSelected, setSelected } = useMemo(() => {
    let isSelected = () => false;
    let setSelected;

    if (selectable) {
      const valueFn = idAttr ? x => x[idAttr] : x => x;
      isSelected = x => {
        const xValue = valueFn(x);
        return xValue === propSelected;
      };
      setSelected = x => propSetSelected(valueFn(x));
    }

    return { isSelected, setSelected };
  }, [selectable, idAttr, propSelected]);

  return {
    lookup,
    isSelected,
    setSelected
  };
}

WorkTray.rootSchema = {
  definitions: {
    mapFieldField: {
      type: ["string", "object"],
      "ui:field": "JsonField",
      showTree: true
    },
    requireRestrictfield: {
      type: "array",
      ":classNames": "from-col-1-size-2",
      "ui:arrayType": "cards",
      items: {
        type: "object",
        "ui:addButtonText": "Add Option",
        "ui:positionButtons": "top-outside",
        properties: {
          flag: {
            title: t`User Flag`,
            type: "string"
          },
          role: {
            title: t`Role`,
            type: "string",
            "ui:field": "LookupFormField",
            lookup: {
              api: "roles",
              resource: t`Roles`,
              id: "name",
              label: "name"
            }
          },
          permission: {
            title: t`Permission`,
            type: "string",
            enum: ["canWorkAs", "canBeAssigned", "canAssign"],
            enumNames: [t`Can Work`, t`Can be Assigned`, t`Can Assign`]
          }
        },
        "ui:field": "ToggleObjectField"
      }
    },
    actionsField: {
      type: "array",
      title: "Actions",
      "ui:arrayType": "cards",
      "ui:newRow": true,
      items: {
        type: "object",
        "ui:addButtonText": "Add Action",
        "ui:positionButtons": "top",
        "ui:sectionType": "subsubsection",
        properties: {
          obj: {
            type: "object",
            ":classNames": "grid-2-columns",
            title: ["# ${$index + 1}: ${label}"].join(""),
            "ui:newSection": true,
            "ui:expandable": true,
            properties: {
              label: {
                title: "Action",
                type: "string",
                "map:field": "label"
              },
              backgroundColor: {
                title: "Color",
                type: "string",
                "map:field": "backgroundColor"
              },
              route: {
                title: "Route",
                type: "string",
                "map:field": "route",
              },
              openRouteInNewTab: {
                title: "Open route in new tab",
                type: "boolean",
                "map:field": "openRouteInNewTab",
                "ui:field": "CheckboxField",
                default: true,
                "ui:showIf": {
                  scope: ".",
                  expr: "$isTruthy(route)",
                },
              },
              routeExpr: {
                title: "routeExpr",
                ":classNames": "from-col-1-size-1",
                type: ["string", "object"],
                "ui:field": "JsonField",
                "map:field": "routeExpr"
              },
              requireExpr: {
                title: "requireExpr",
                type: ["string", "object"],
                "ui:field": "JsonField",
                "map:field": "requireExpr"
              },
              requirerestrict: {
                type: "object",
                ":classNames": "from-col-1-size-2",
                title: "Conditions",
                "ui:sectionType": "label",
                "ui:field": "ToggleObjectField",
                properties: {
                  require: {
                    title: "Require Any",
                    "map:field": "require",
                    $$include: "#/definitions/requireRestrictfield"
                  },
                  restrict: {
                    title: "Restrict If Any",
                    "map:field": "restrict",
                    $$include: "#/definitions/requireRestrictfield"
                  }
                }
              }
            }
          }
        }
      }
    }
  },
  properties: {
    title: {
      title: "Title",
      type: "string",
      "map:field": "title"
    },
    className: {
      title: "Class Names",
      type: "string",
      "map:field": ":classNames"
    },
    eT: {
      type: "object",
      title: " ",
      properties: {
        entityType: {
          title: "Entity Type",
          type: "string",
          "map:field": "entityType",
          "ui:field": "LookupFormField",
          lookup: {
            resource: "Entity Types",
            api: "api:manage/objectschemas",
            params: { all: true },
            options: { useCache: true },
            jnx: "data.items",
            id: "name",
            label: "name"
          }
        },
        otherEntityTypes: {
          title: " ",
          type: "array",
          "map:field": "otherEntityTypes",
          "ui:sectionType": "label",
          items: {
            "ui:field": "LookupFormField",
            title: " ",
            required: false,
            min: 0,
            "ui:orderable": false,
            type: "string",
            lookup: {
              resource: "Entity Types",
              api: "api:manage/objectschemas",
              params: { all: true },
              options: { useCache: true },
              jnx: "data.items",
              id: "name",
              label: "name"
            }
          }
        },
        useMainRoleEntities: {
          title: "Show all entity types from main role",
          type: "boolean",
          "map:field": "useMainRoleEntities"
        }
      }
    },
    resource: {
      title: "Resource",
      type: "string",
      "map:field": "resource"
    },
    lookupParams: {
      title: "Params",
      type: "object",
      "map:field": "lookupParams",
      properties: {},
      additionalProperties: {
        type: "string",
        title: "value"
      },
      "ui:asPropertiesTable": true
    },
    tableActions: {
      $$include: "#/definitions/actionsField",
      ":classNames": "from-col-1-size-3",
      "map:array": "tableActions"
    },
    defaultFilter: {
      title: "Default Filter",
      type: "string",
      "map:field": "defaultFilter"
    },
    filters: {
      title: "Filters",
      ":classNames": "from-col-1-size-3",
      type: ["string", "object"],
      "ui:field": "JsonField",
      "map:field": "filters"
    },
    columns: {
      title: "Columns",
      ":classNames": "from-col-1-size-3",
      type: "array",
      "map:array": "columns",
      items: {
        type: "object",
        "ui:showColumnsIf": {
          actions: {
            expr: "hasActions",
            debug: "actions showColumnsIf",
            scope: ".."
          }
        },
        properties: {
          title: {
            type: "string",
            "map:field": "title",
            title: "Title"
          },
          sortkey: {
            type: "string",
            "map:field": "sortkey",
            title: "Key"
          },
          mapField: {
            $$include: "#/definitions/mapFieldField",
            "map:field": "map:field",
            title: "Field"
          },
          format: {
            type: "string",
            "map:field": "format",
            title: "format"
          },
          "hideColumn": {
            "type": "boolean",
            "map:field": "hideColumn",
            "title": "Hide"
          },
          "exportable": {
            "type": "boolean",
            "map:field": "exportable",
            "title": "Exportable"
          },
          panel2: {
            "ui:newRow": true,
            type: "object",
            title: "Extra Properties...",
            "ui:field": "HideObjectField",
            "ui:newSection": true,
            "ui:sectionType": "subsubsection",
            "ui:expandable": true,
            "ui:expandedDefault": false,
            ":classNames": "grid-2-columns bordered",
            properties: {
              color: {
                $$include: "#/definitions/mapFieldField",
                "map:field": "color",
                title: "Color"
              },
              valueTranform: {
                $$include: "#/definitions/mapFieldField",
                "map:field": "valueTranform",
                title: "Value Tranform Expression"
              },
              mapImage: {
                $$include: "#/definitions/mapFieldField",
                ":classNames": "from-col-1-size-1",
                "map:field": "map:image",
                title: "Image"
              },
              iconSize: {
                title: "Icon Size",
                "ui:showIf": {
                  scope: ".",
                  expr: "$isTruthy(mapImage)",
                  debug: "iconSize showIf"
                },
                type: "string",
                enum: ["1em", "2em", "4em", "6em", "8em", "10em"],
                "map:field": { default: "icons.size", path: "fontSize" }
              },
              type: {
                $$include: "#/definitions/mapFieldField",
                "map:field": "type",
                title: "Type"
              },
              actions: {
                $$include: "#/definitions/actionsField",
                ":classNames": "from-col-1-size-2",
                "map:array": "actions"
              }
            }
          }
        }
      }
    }
  }
};

function DataRow({
  rowObject,
  columns,
  scope,
  selected,
  setSelected,
  openModal,
  onSearch,
  loaderFnAction
}) {
  const fnBindings = {
    onSearch,
    openModal,
    loaderFnAction
  };

  return (
    <tr>
      {columns.reduce((acc, cell, idx) => {
        const isAction = Array.isArray(cell.actions) && cell.actions.length > 0;
        if (!cell.hideColumn) {
          acc.push(
            <td key={idx} style={cell.style}>
              {isAction ? (
                <div className="worktray-action-btns">
                  <ActionButtonsCell
                    actions={cell.actions}
                    rowObject={rowObject}
                    scope={scope}
                  />
                </div>
              ) : (
                <DataCell
                  cell={cell}
                  rowObject={rowObject}
                  selected={selected}
                  setSelected={setSelected}
                  scope={scope}
                />
              )}
            </td>
          );
        }
        return acc;
      }, [])}
    </tr>
  );
}

function MobileDataRow({ rowObject, columns, scope, selected, setSelected }) {
  return (
    <Card>
      <CardBody>
        {columns
          .filter(({ mobilePosition }) => mobilePosition > 0 || 1)
          .reduce((acc, cell, idx) =>{
            if (!cell.hideColumn) {
              acc.push(cell.actions ? (
                <ActionButtonsCell
                  key={idx}
                  actions={cell.actions}
                  rowObject={rowObject}
                  scope={scope}
                />
              ) : (
                <CardText key={idx}>
                  <MobileDataCell
                    cell={cell}
                    rowObject={rowObject}
                    selected={selected}
                    setSelected={setSelected}
                  />
                </CardText>
              ))
            }
            return acc
          }, [])}
      </CardBody>
    </Card>
  );
}

function MobileDataCell({ rowObject, cell, selected, setSelected }) {
  const {
    "map:field": mapField,
    "map:image": mapImage,
    iconSize,
    format,
    type,
    checkbox,
    color: colorExpr,
    title
  } = cell;
  const emptyText = cell.emptyText || (checkbox ? "" : "---");

  const colorJnx = useJnx(colorExpr);

  const value = useMemo(
    () => (mapField ? mapObject(rowObject, { value: mapField }).value : null),
    [rowObject, mapField]
  );
  const color = useMemo(() => colorJnx?.eval(rowObject, "", { value }), [
    value,
    colorJnx,
    rowObject
  ]);
  const formatter = useFormatter(format);
  const text = useMemo(
    () =>
      formatter !== IDENTITY_FORMATTER
        ? formatter.apply(value)
        : value !== null && value !== undefined
        ? `${value}`
        : "",
    [value, formatter]
  );
  const image = useMemo(
    () => (mapImage ? mapObject(rowObject, { value: mapImage }).value : null),
    [rowObject, mapImage]
  );

  const onClick = useCallback(
    e => {
      e.preventDefault();
      setSelected(rowObject);
    },
    [rowObject, setSelected]
  );

  const flag = mapField ? value : selected;

  return (
    <>
      {image ? <ElementIcon element={image} fontSize={iconSize} /> : null}
      {image ? " " : null}
      {checkbox ? (
        <button
          className={`icon-check-field btn ${
            flag ? "btn-success" : "btn-secondary"
          }`}
          color="secondary"
          onClick={onClick}
        >
          {flag ? <i className="fa fa-check" /> : null}
        </button>
      ) : null}
      {type === "badge" ? (
        <Badge color={color} style={{ fontSize: "100%" }} pill>
          {text || emptyText}
        </Badge>
      ) : (
        `${title}${title ? ":" : ""} ${text}` || emptyText || ""
      )}
    </>
  );
}

function DataCell({ rowObject, cell, selected, setSelected, scope }) {
  const { "map:field": mapField, valueTranform: valueTranformExpr } = cell;

  const valueTranformJnx = useJnx(valueTranformExpr);

  const value = useMemo(() => {
    let value = mapField
      ? mapObject(rowObject, { value: mapField }, undefined, scope).value
      : null;

    if (valueTranformJnx)
      value = valueTranformJnx.eval(rowObject, "", { value, scope });

    return Array.isArray(value) ? value : [value];
  }, [rowObject, mapField, valueTranformJnx, scope]);

  return value.map((item, index) => (
    <DataCellValue
      key={index}
      value={item}
      rowObject={rowObject}
      cell={cell}
      selected={selected}
      setSelected={setSelected}
      scope={scope}
    />
  ));
}

function DataCellValue({ value, rowObject, cell, selected, setSelected }) {
  const {
    "map:field": mapField,
    "map:image": mapImage,
    iconSize,
    format,
    type,
    checkbox,
    color: colorExpr
  } = cell;
  const emptyText = cell.emptyText || (checkbox ? "" : "---");

  const colorJnx = useJnx(colorExpr);

  const color = useMemo(() => colorJnx?.eval(rowObject, "", { value }), [
    value,
    colorJnx,
    rowObject
  ]);
  const formatter = useFormatter(format);
  const text = useMemo(
    () =>
      formatter !== IDENTITY_FORMATTER
        ? formatter.apply(value)
        : value !== null && value !== undefined
        ? `${value}`
        : "",
    [value, formatter]
  );
  const image = useMemo(
    () => (mapImage ? mapObject(rowObject, { value: mapImage }).value : null),
    [rowObject, mapImage]
  );

  const onClick = useCallback(
    e => {
      e.preventDefault();
      setSelected(rowObject);
    },
    [rowObject, setSelected]
  );

  const flag = mapField ? value : selected;
  let result = (
    <>
      {image ? <ElementIcon element={image} fontSize={iconSize} /> : null}
      {image ? " " : null}
      {checkbox ? (
        <button
          className={`icon-check-field btn ${
            flag ? "btn-success" : "btn-secondary"
          }`}
          color="secondary"
          onClick={onClick}
        >
          {flag ? <i className="fa fa-check" /> : null}
        </button>
      ) : null}
      {text || emptyText || ""}
    </>
  );

  switch (type) {
    case "button":
      result = <Button color={color}>{result}</Button>;
      break;
    case "badge":
      result = (
        <Badge color={color} style={{ fontSize: "100%" }} pill>
          {result}
        </Badge>
      );
      break;
    case "square-badge":
      result = (
        <Badge color={color} style={{ fontSize: "100%" }}>
          {result}
        </Badge>
      );
      break;
  }

  return <div>{result}</div>;
}

const BUTTONS_COMPONENTS = {
  confirmbutton: ConfirmButton,
  reload: ReloadButton
};

function ActionButtonsCell({ actions, rowObject, scope, fnBindings }) {
  const auth = useContext(AuthContext);

  const buttons = useMemo(
    () =>
      actions.map(obj => {
        const newObj = { ...obj };
        if (obj.requireExpr) {
          newObj.requireJnx = new Jnx(obj.requireExpr);
        }
        return newObj;
      }),
    [actions]
  );

  const filteredActions = useMemo(
    () =>
      buttons.filter(obj => {
        return (
          (!obj.requireJnx || obj.requireJnx.eval(rowObject, "", scope)) &&
          (!obj.require ||
            obj.require.some(requirement =>
              checkButtonAccess(requirement, rowObject, auth)
            )) &&
          (!obj.restrict ||
            obj.restrict.every(
              requirement => !checkButtonAccess(requirement, rowObject, auth)
            ))
        );
      }),
    [auth, rowObject, scope, buttons]
  );

  return (
    <>
      {filteredActions.map((action, idx) => {
        const Component =
          BUTTONS_COMPONENTS[action?.type?.toLowerCase()] || ActionButton;
        return (
          <Component
            fn={fnBindings}
            key={idx}
            action={action}
            rowObject={rowObject}
            scope={scope}
          />
        );
      })}
    </>
  );
}

function checkButtonAccess({ role, permission, flag }, rowObject, auth) {
  return (
    (role ? checkAccess(auth, role, permission) : true) &&
    (flag ? getObject(rowObject, flag) : true)
  );
}

function ActionButton({ action, rowObject, scope: propScope }) {
  const [loading, error, loadFn] = useLoader();
  const scope = useMemo(
    () => ({
      id: rowObject?.id,
      entityTypeSlug: rowObject?.entityTypeSlug,
      item: rowObject,
      ...propScope,
      loadFn
    }),
    [rowObject, propScope, loadFn]
  );

  const route = useRouteInterpolation(action.route, action.routeExpr, scope);
  const onClick = useMemo(() => {
    if (!action.onClick) return null;
    if (typeof action.onClick === "function") return action.onClick;
    const jnx = new Jnx(action.onClick);
    return (rowObject, scope) => jnx.eval(rowObject, "", { scope });
  }, [action.onClick]);

  const bgColor = action.backgroundColor || null;
  const borderColor = action.borderColor ?? bgColor;

  let dataCy = `btn_${action.label}`;
  if (scope?.id) dataCy += `_${scope.id}`;

  if (loading) return <Loader />
  if (route) return !action.openRouteInNewTab ? (
    <Link
      style={{ backgroundColor: bgColor, borderColor }}
      className="btn btn-primary worktray-action-btn"
      data-cy={dataCy}
      href={route}
      to={route}
    >
      {action.label}
    </Link>
  ) : (
    <a
      style={{ backgroundColor: bgColor, borderColor }}
      className="btn btn-primary worktray-action-btn"
      data-cy={dataCy}
      href={route}
      rel="noopener noreferrer"
      target="_blank"
      onClick={(e) => {
        openInNewTab(route);
      }}
    >
      {action.label}
    </a>
  )

  return onClick ? (
    <Button
      style={{
        backgroundColor: action.backgroundColor ? action.backgroundColor : null
      }}
      color="primary"
      data-cy={dataCy}
      onClick={() => onClick(rowObject, scope)}
    >
      {action.label}
    </Button>
  ) : null
}

async function onConfirm({ confirm, fn }) {
  const WORKFLOW_SELECT = () => {
    return ["ConfirmationModal", confirm?.title || ``, confirm?.text || ``];
  };

  const wf = await fn.openModal(...WORKFLOW_SELECT());
  if (wf) {
    fn.loaderFnAction(true);
    const response = await UweApi[confirm.method]({ api: confirm.api });
    if (response) fn.onSearch();
    fn.loaderFnAction(false);
  }
}

function ConfirmButton({ action, rowObject, scope: propScope, fn }) {
  const scope = useMemo(
    () => ({
      id: rowObject?.id,
      entityTypeSlug: rowObject?.entityTypeSlug,
      item: rowObject,
      ...propScope
    }),
    [rowObject, propScope]
  );

  const api = interpolate(action.confirm.api, scope);

  let dataCy = `btn_confirm_${action.label}`;
  if (scope?.id) dataCy += `_${scope.id}`;

  return (
    <Button
      className="btn btn-primary"
      color=""
      data-cy={dataCy}
      onClick={() => onConfirm({ fn, confirm: { ...action?.confirm, api } })}
    >
      {action.label}
    </Button>
  );
}

const openInNewTab = (url) => {
  window.open(url, "_blank");
};

const DEFAULT_PERIODS = [1, 5, 15, 30, 60, 0];
function ReloadButton({ action, scope }) {
  const { period: defaultPeriod, periods: _periods } = action;
  const periods = _periods || DEFAULT_PERIODS;
  const [period, setPeriod] = useState(defaultPeriod);
  const paused = !period;
  const { lastReload, reload } = scope;
  const nextReload = useMemo(
    () => (lastReload ?? new Date().getTime()) + period * 1000,
    [period, lastReload]
  );
  const now = new Date().getTime();

  let dataCy = `btn_reload_${action.label}`;
  if (scope?.id) dataCy += `_${scope.id}`;

  useEffect(() => {
    if (paused) return;
    const timeLeft = nextReload - new Date().getTime();
    if (timeLeft < 0) {
      reload();
    } else {
      const timeout = setTimeout(() => {
        reload();
      }, Math.max(timeLeft, 10));
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [paused, nextReload]);

  return (
    <ButtonGroup>
      <Button
        className="btn btn-primary"
        color=""
        data-cy={dataCy}
        onClick={() => reload()}
      >
        {action.label || <i className="fa fa-redo" />}
      </Button>
      <UncontrolledDropdown className="user-nav-dropdown">
        <DropdownToggle className="btn btn-primary" color="" caret nav>
          {period ? `${period}s` : "Off"}
        </DropdownToggle>
        <DropdownMenu right>
          {periods.map((period, index) => (
            <DropdownItem key={index} onClick={() => setPeriod(period)}>
              {period ? `${period}s` : "Off"}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
    </ButtonGroup>
  );
}

const parseSearchFormDefinition = ({ filters }) => {
  if (filters) {
    const sFilters = {};
    const schema = filters.reduce((_, { key, ...obj }) => {
      _[key] = obj;
      return _;
    }, {});
    return [
      parseFormDefinition({
        schemaProps: {
          // ":classNames": "smallThreeRows",
        },
        schema
      }),
      sFilters
    ];
  }
  return [];
};

export default WorkTray;

/**
 * Temporary exports pending refactor.
 * @see {@link module:components/Dashboard/util/FilterForm}
 */
export { parseSearchFormDefinition };
